import { EthernetIcon, NoCameraIcon } from "@/components/icons";
import { Button } from "@/modules/shadcn/ui/button";
import { Spinner } from "@/modules/shadcn/ui/spinner";
import { RefreshCw, Usb } from "lucide-react";
import type { ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";

export interface NoCameraDetectedProps {
  onRefresh: () => void;
  isRefreshing?: boolean;
  onOpenSettings?: () => void;
  /**
   * Tightens spacing and type so the screen fits inside the 16:9 preview box
   * (Run → Inference) instead of owning the whole page (Capture).
   */
  compact?: boolean;
}

export const NoCameraDetected = ({
  onRefresh,
  isRefreshing,
  onOpenSettings,
  compact,
}: NoCameraDetectedProps) => {
  const { t } = useTranslation(["camera", "common"]);
  const cx = {
    root: compact
      ? "flex h-full flex-1 flex-col items-center justify-center overflow-y-auto p-6 text-center"
      : "flex flex-1 flex-col items-center justify-center p-8",
    icon: compact
      ? "mb-4 h-14 w-14 [&_svg]:size-6"
      : "mb-6 h-20 w-20 [&_svg]:size-8",
    title: compact ? "mb-1 text-base" : "mb-2 text-xl",
    description: compact ? "mb-4 text-xs" : "mb-6 text-sm",
    badges: compact ? "mb-4 gap-2" : "mb-6 gap-4",
    badge: compact
      ? "px-3 py-1.5 text-xs [&_svg]:size-4"
      : "px-4 py-2 text-sm [&_svg]:size-5",
    button: compact ? "mb-4" : "mb-8",
    settings: compact ? "mb-2 text-xs" : "mb-4 text-sm",
    link: compact ? "text-xs" : "text-sm",
  };

  const ConnectionBadge = ({
    icon,
    label,
  }: {
    icon: ReactNode;
    label: string;
  }) => (
    <div
      className={`flex items-center gap-2 rounded-lg border border-black/10 [&_svg]:text-black/60 ${cx.badge}`}
    >
      {icon}
      <span>{label}</span>
      <span className="font-medium text-destructive">
        {t("connection.noCameraDetected.notDetected")}
      </span>
    </div>
  );

  return (
    <div className={cx.root}>
      <div
        className={`flex items-center justify-center rounded-full bg-destructive/10 [&_svg]:text-destructive ${cx.icon}`}
      >
        <NoCameraIcon />
      </div>

      <p className={`font-semibold text-black ${cx.title}`}>
        {t("connection.noCameraDetected.title")}
      </p>

      <p className={`text-black/60 ${cx.description}`}>
        {t("connection.noCameraDetected.description")}
      </p>

      <div className={`flex flex-row ${cx.badges}`}>
        <ConnectionBadge icon={<Usb />} label="USB" />
        <ConnectionBadge
          icon={<EthernetIcon />}
          label={t("connection.noCameraDetected.ethernet")}
        />
      </div>

      <Button
        onClick={onRefresh}
        disabled={isRefreshing}
        size={compact ? "sm" : "default"}
        className={cx.button}
      >
        {isRefreshing ? <Spinner /> : <RefreshCw />}
        {t("common:actions.refresh")}
      </Button>

      {onOpenSettings && (
        <p className={`text-black/60 ${cx.settings}`}>
          <Trans
            t={t}
            i18nKey="connection.noCameraDetected.wrongAddress"
            components={{
              button: (
                <button
                  type="button"
                  className="cursor-pointer font-medium underline hover:text-foreground"
                  onClick={onOpenSettings}
                />
              ),
            }}
          />
        </p>
      )}

      <a
        href="https://robopipe.gitbook.io/doc/getting-started/connection"
        target="_blank"
        rel="noopener noreferrer"
        className={`text-center text-primary hover:underline ${cx.link}`}
      >
        {t("connection.learnMore")}
      </a>
    </div>
  );
};
